import { get, reduce } from 'lodash-es'
import { mapState } from 'pinia'

import PanelLoading from 'src/components/panel_loading/panel_loading.vue'
import RichContent from 'src/components/rich_content/rich_content.jsx'

import { useMergedConfigStore } from 'src/stores/merged_config.js'
import { useStatusesStore } from 'src/stores/statuses.js'
import { useStreamingStore } from 'src/stores/streaming.js'

import { fetchConversation, fetchStatus } from 'src/api/public.js'
import { WSConnectionStatus } from 'src/api/websocket.js'
import { observeHeight } from 'src/services/height_observer/height_observer.js'
import { compareIds, isNumericId } from 'src/services/ids/ids.js'

// oldest first: numeric (Mastodon) ids before text ones, each exactly
const sortById = (a, b) => {
  const idA = a.type === 'retweet' ? a.retweeted_status.id : a.id
  const idB = b.type === 'retweet' ? b.retweeted_status.id : b.id
  const numericA = isNumericId(idA)
  if (numericA !== isNumericId(idB)) return numericA ? -1 : 1
  return compareIds(idA, idB)
}

const conversation = {
  props: {
    statusId: {
      // Main thing
      type: String,
      required: true,
    },
    collapsable: {
      // Whether conversation can be collapsed
      // i.e. when it's not a page
      type: Boolean,
      default: false,
    },
    isPage: {
      // Whether conversation is rendered as a standalone page
      // as opposed to embedded into a timeline
      type: Boolean,
      default: false,
    },
    inProfile: {
      // Whether conversation is rendered in a user profile
      // used for overriding muted status
      type: Boolean,
      default: false,
    },
    // for the server's word filters, see Status
    filterPlace: { type: String, default: null },
    profileUserId: {
      // used with inProfile, user id of the profile
      type: String,
      default: null,
    },
    virtualHidden: {
      // Whether conversation is suspended. Controls rendering of statuses
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      focused: null,
      expanded: false,
      loadStatusError: null,
      unsuspendibleIds: new Set(),
      virtualHeight: 120,
    }
  },
  created() {
    if (this.isPage) {
      this.fetchConversation()
    }
  },
  beforeUnmount() {
    this.setBody(null)
  },
  computed: {
    status() {
      return useStatusesStore().allStatuses.get(this.statusId)
    },
    streamingEnabled() {
      return (
        this.mergedConfig.useStreamingApi &&
        this.mastoUserSocketStatus === WSConnectionStatus.JOINED
      )
    },
    suspendable() {
      return this.unsuspendibleIds.size === 0
    },
    hide() {
      return this.virtualHidden && this.suspendable
    },
    originalStatusId() {
      if (this.status.retweeted_status) {
        return this.status.retweeted_status.id
      } else {
        return this.statusId
      }
    },
    conversationId() {
      return this.getConversationId(this.statusId)
    },
    conversation() {
      if (!this.status) {
        return []
      }

      if (!this.isExpanded) {
        return [this.status]
      }

      const conversation = useStatusesStore().conversations.get(
        this.conversationId,
      )

      return [...conversation.keys()]
        .map((k) => useStatusesStore().allStatuses.get(k))
        .filter((status) => status.type != 'repeat') // Old backend behavior?
        .toSorted(sortById)
    },
    replies() {
      let i = 1

      return reduce(
        this.conversation,
        (result, { id, in_reply_to_status_id: irid }) => {
          if (irid) {
            result[irid] = result[irid] || []
            result[irid].push({
              name: `#${i}`,
              id,
            })
          }
          i++
          return result
        },
        {},
      )
    },
    isExpanded() {
      return !!(this.expanded || this.isPage)
    },
    hiddenStyle() {
      return { height: this.virtualHeight + 'px' }
    },
    maybeFocused() {
      return this.isExpanded ? this.focused : null
    },
    ...mapState(useMergedConfigStore, ['mergedConfig']),
    ...mapState(useStreamingStore, {
      mastoUserSocketStatus: (state) => state.state,
    }),
  },
  components: {
    RichContent,
    PanelLoading,
  },
  watch: {
    statusId(newVal, oldVal) {
      const newConversationId = this.getConversationId(newVal)
      const oldConversationId = this.getConversationId(oldVal)
      if (
        newConversationId &&
        oldConversationId &&
        newConversationId === oldConversationId
      ) {
        this.setFocused(this.originalStatusId)
      } else {
        this.fetchConversation()
      }
    },
    expanded(value) {
      if (value) this.fetchConversation()
    },
  },
  methods: {
    fetchConversation() {
      if (this.status) {
        fetchConversation({
          id: this.statusId,
        }).then(({ data: { ancestors, descendants }, timestamp }) => {
          useStatusesStore().addNewStatuses({ statuses: ancestors, timestamp })
          useStatusesStore().addNewStatuses({
            statuses: descendants,
            timestamp,
          })
          this.setFocused(this.originalStatusId)
        })
      } else {
        this.loadStatusError = null
        fetchStatus({
          id: this.statusId,
        })
          .then(({ data: status }) => {
            useStatusesStore().addNewStatuses({ statuses: [status] })
            this.fetchConversation()
          })
          .catch((error) => {
            console.error(error)
            this.loadStatusError = error
          })
      }
    },
    getReplies(id) {
      return this.replies[id] || []
    },
    setFocused(id) {
      if (!id) return
      this.focused = id

      if (!this.streamingEnabled) {
        useStatusesStore().fetchStatus(id)
      }

      useStatusesStore().fetchFavsAndRepeats(id)
      useStatusesStore().fetchEmojiReactions(id)
    },
    toggleExpanded() {
      this.expanded = !this.expanded
    },
    getConversationId(statusId) {
      const status = useStatusesStore().allStatuses.get(statusId)
      return get(
        status,
        'retweeted_status.statusnet_conversation_id',
        get(status, 'statusnet_conversation_id'),
      )
    },
    onStatusSuspendStateChange({ id, suspend }) {
      if (!suspend) {
        this.unsuspendibleIds.add(id)
      } else {
        this.unsuspendibleIds.delete(id)
      }
    },
    // the body's height, kept for the placeholder while it is hidden
    setBody(element) {
      if (element === this.bodyElement) return
      this.stopObservingBody?.()
      this.bodyElement = element
      this.stopObservingBody =
        element &&
        observeHeight(element, (height) => {
          // 0 while an ancestor is display: none; keep the real height
          if (height > 0) this.virtualHeight = height
        })
    },
  },
}

export default conversation
